'use client'

import { useEffect, useMemo } from 'react'
import { z } from 'zod'
import { create } from 'zustand'

import { highlightFragments } from '@/features/veluvit/components/reader/reader-blocks'
import { useSessionStore } from '@/features/veluvit/stores/session.store'

export type ExcerptRatio = '1:1' | '3:4' | '9:16'

const paragraphSchema = z.object({
  kind: z.enum(['speech', 'narration']),
  text: z.string(),
})
const swapSchema = z.object({
  id: z.string(),
  from: z.string(),
  to: z.string(),
})
const styleSchema = z.object({
  bg: z.enum(['scene', 'white', 'black', 'photo']),
  /** 작품 장면 · 표지 주소(bg = scene) */
  scene: z.string().optional(),
  /** 내 사진(bg = photo) — 기기에서 줄인 JPEG data URL. 서버 연동 뒤엔 업로드 주소로 바뀐다 */
  photo: z.string().optional(),
  ratio: z.enum(['1:1', '3:4', '9:16']),
  font: z.enum(['serif', 'sans', 'display']),
  /** 글자 크기 1~7 */
  size: z.number().int().min(1).max(7),
  color: z.enum(['black', 'white']),
  align: z.enum(['left', 'center', 'right']),
  maskOn: z.boolean(),
  mask: z.enum(['blur', 'initial', 'replace']),
  maskText: z.string(),
  /** 키워드 치환 — 원본 → 치환, 최대 5줄 */
  swaps: z.array(swapSchema).max(5),
})
const excerptSchema = z.object({
  id: z.string(),
  roomId: z.string().optional(),
  workId: z.string(),
  messageId: z.string().optional(),
  /** 발췌한 글 원문(문단은 빈 줄로 잇는다) — 형광펜 · 서버 저장용 */
  text: z.string(),
  paragraphs: z.array(paragraphSchema),
  style: styleSchema,
  charName: z.string(),
  author: z.string(),
  /** 그때의 이용자 이름 — 가리기를 다시 그릴 때 쓴다 */
  userName: z.string(),
  createdAt: z.string(),
})

export type ExcerptParagraph = z.infer<typeof paragraphSchema>
export type ExcerptStyle = z.infer<typeof styleSchema>
export type Excerpt = z.infer<typeof excerptSchema>
type ExcerptInput = Omit<Excerpt, 'id' | 'createdAt' | 'text'>

export const EXCERPT_MAX_SWAPS = 5

/* ---------- [임시] 기기 저장소 ---------- */

type LocalState = { owner: string | null; items: Excerpt[] }
const useExcerptStore = create<LocalState>()(() => ({ owner: null, items: [] }))

const keyOf = (owner: string) => `veluvit_excerpts:${owner}`
const ownerNow = () => useSessionStore.getState().data?.user?.id || 'guest'

/** 지금 계정의 칸을 읽는다 — 계정이 바뀌었으면 다시 읽는다. 저장값은 믿을 수 없는 경계라 검사해서 맞는 것만 남긴다 */
function load(): Excerpt[] {
  const owner = ownerNow()
  const state = useExcerptStore.getState()
  if (state.owner === owner) return state.items
  let items: Excerpt[] = []
  try {
    const raw = JSON.parse(localStorage.getItem(keyOf(owner)) || '[]')
    const parsed = z.array(z.unknown()).catch([]).parse(raw)
    items = parsed.flatMap((x) => {
      const one = excerptSchema.safeParse(x)
      return one.success ? [one.data] : []
    })
  } catch {
    items = []
  }
  useExcerptStore.setState({ owner, items })
  return items
}

function persist(items: Excerpt[]) {
  try {
    localStorage.setItem(keyOf(ownerNow()), JSON.stringify(items))
  } catch {
    throw new Error(
      '기기 저장 공간이 모자라요. 내 사진 대신 다른 배경으로 저장해 보세요.'
    )
  }
  useExcerptStore.setState({ owner: ownerNow(), items })
}

const newId = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`

/* ---------- 서비스 ---------- */

/** 내 기기의 발췌함에 보관한다. */
export async function saveExcerpt(input: ExcerptInput): Promise<Excerpt> {
  const excerpt: Excerpt = {
    ...input,
    id: newId(),
    text: input.paragraphs.map((p) => p.text).join('\n\n'),
    createdAt: new Date().toISOString(),
  }
  persist([excerpt, ...load()])
  return excerpt
}

/** 내 기기의 발췌함 목록을 최근 순으로 읽는다. */
export async function listExcerpts(): Promise<Excerpt[]> {
  return load()
}

/** 내 기기에 보관한 발췌를 지운다. */
export async function deleteExcerpt(id: string): Promise<void> {
  persist(load().filter((x) => x.id !== id))
}

/**
 * 이 방에서 발췌한 문장 — 리더의 형광펜 조각(highlightFragments).
 * 기기에 보관한 실제 발췌만 사용한다.
 */
export function useRoomHighlights(roomId: string | undefined): string[] {
  const owner = useSessionStore((s) => s.data?.user?.id || 'guest')
  const items = useExcerptStore((s) => s.items)
  const storedOwner = useExcerptStore((s) => s.owner)
  useEffect(() => {
    load()
  }, [owner])
  return useMemo(
    () =>
      roomId && storedOwner === owner
        ? highlightFragments(
            items.filter((x) => x.roomId === roomId).map((x) => x.text)
          )
        : [],
    [items, roomId, storedOwner, owner]
  )
}
